import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Pencil, Ban, CheckCircle, Trash2, ArrowUpDown } from 'lucide-react';
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";

const UserTable = ({ users, onViewUser, onEditUser, onDeactivateUser, onActivateUser, onDeleteUser }) => {
   const [sortDirection, setSortDirection] = useState('asc');

   const sortedUsers = [...users].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return sortDirection === 'asc'
         ? nameA.localeCompare(nameB)
         : nameB.localeCompare(nameA);
   });

   const toggleSort = () => {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
   };

   const renderActionButtons = (user) => {
      const actions = [
         {
            icon: Eye,
            label: "View Details",
            onClick: () => onViewUser(user),
            variant: "outline"
         },
         {
            icon: Pencil,
            label: "Edit User",
            onClick: () => onEditUser(user),
            variant: "outline"
         },
         user.isActive ? {
            icon: Ban,
            label: "Deactivate User",
            onClick: () => onDeactivateUser(user),
            variant: "outline",
            className: "text-yellow-600 hover:text-yellow-700"
         } : {
            icon: CheckCircle,
            label: "Activate User",
            onClick: () => onActivateUser(user),
            variant: "outline",
            className: "text-green-600 hover:text-green-700"
         },
         // commented out for pre-deployment preparation
         // {
         //    icon: Trash2,
         //    label: "Delete User",
         //    onClick: () => onDeleteUser(user),
         //    variant: "outline",
         //    className: "text-red-600 hover:text-red-700"
         // }
      ];

      return actions.map((action, index) => (
         <TooltipProvider key={index}>
            <Tooltip>
               <TooltipTrigger asChild>
                  <Button
                     variant={action.variant}
                     size="icon"
                     onClick={action.onClick}
                     className={`h-8 w-8 ${action.className || ''}`}
                  >
                     <action.icon className="h-4 w-4" />
                  </Button>
               </TooltipTrigger>
               <TooltipContent>
                  <p>{action.label}</p>
               </TooltipContent>
            </Tooltip>
         </TooltipProvider>
      ));
   };

   return (
      <Table>
         <TableHeader>
            <TableRow>
               <TableHead>Username</TableHead>
               <TableHead>
                  <Button
                     variant="ghost"
                     onClick={toggleSort}
                     className="hover:bg-transparent p-0 font-medium flex items-center gap-1"
                  >
                     Name
                     <ArrowUpDown className="h-4 w-4" />
                  </Button>
               </TableHead>
               <TableHead>Email</TableHead>
               <TableHead>Roles</TableHead>
               <TableHead>Status</TableHead>
               <TableHead className="text-right">Actions</TableHead>
            </TableRow>
         </TableHeader>
         <TableBody>
            {sortedUsers?.map((user) => (
               <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                     {user.firstName} {user.lastName}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                     {user.roles?.map((role) => (
                        <Badge
                           key={role}
                           variant="secondary"
                           className="bg-secondary/50 mr-1"
                        >
                           {role}
                        </Badge>
                     ))}
                  </TableCell>
                  <TableCell>
                     <Badge
                        variant={user.isActive ? "success" : "destructive"}
                        className={user.isActive ?
                           "bg-green-100 text-green-800 hover:bg-green-100/80" :
                           "bg-red-100 text-red-800 hover:bg-red-100/80"
                        }
                     >
                        {user.isActive ? 'Active' : 'Inactive'}
                     </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                     <div className="flex justify-end gap-2">
                        {renderActionButtons(user)}
                     </div>
                  </TableCell>
               </TableRow>
            ))}
         </TableBody>
      </Table>
   );
};

export default UserTable;
